// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/ui-core/app-lock-provider.tsx
// Parameters       : none — this file is byte-identical across the fleet
// Destination      : packages/fleet/ui/src/providers/app-lock-provider.tsx
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo apunte-digital-app
//     node fleet/fleet.ts check --repo apunte-digital-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * AppLockProvider — OPTIONAL biometric app-lock (Phase 3).
 *
 * ## OPT-IN, OFF BY DEFAULT
 * This provider is a **pass-through** unless ALL of the following hold:
 *   1. the runtime is native (`isTauriRuntime()` — the biometric plugin is
 *      Tauri-mobile only), AND
 *   2. the per-device setting is ON (`isAppLockEnabled()`, default OFF), AND
 *   3. a biometric / device-credential prompt is actually available.
 */

import { msg as _macroMsg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { invoke } from "@tauri-apps/api/core";
import { createSignal, onCleanup, onMount, type ParentComponent, Show } from "solid-js";
import { Portal } from "solid-js/web";
import { Fingerprint, LogOut } from "@fleet/icons";

export type AppLockState = "unlocked" | "locked" | "authenticating";

const msg =
  typeof _macroMsg === "function"
    ? _macroMsg
    : (strings: TemplateStringsArray | string): MessageDescriptor => {
        const s = typeof strings === "string" ? strings : (strings[0] ?? "");
        return { id: s, message: s };
      };

const toText = (d: unknown): string => {
  if (typeof d === "string") {
    return d;
  }
  if (d && typeof d === "object") {
    if ("message" in d && typeof d.message === "string") {
      return d.message;
    }
    if ("id" in d && typeof d.id === "string") {
      return d.id;
    }
  }
  return typeof d === "number" || typeof d === "boolean" ? String(d) : "";
};

function isTauriRuntime(): boolean {
  return (
    typeof window !== "undefined" && ("__TAURI_INTERNALS__" in window || "__TAURI__" in window)
  );
}

function isAppLockEnabled(): boolean {
  try {
    const storage =
      typeof globalThis !== "undefined" && "localStorage" in globalThis
        ? (globalThis.localStorage as Storage | undefined)
        : undefined;
    if (!storage) {
      return false;
    }
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.endsWith(".app-lock-enabled")) {
        const val = storage.getItem(key);
        return val === "true" || val === "1";
      }
    }
    return false;
  } catch {
    return false;
  }
}

async function checkBiometricAvailable(): Promise<boolean> {
  if (!isTauriRuntime()) {
    return false;
  }
  try {
    const status: unknown = await invoke("plugin:biometric|status");
    if (typeof status === "object" && status !== null && "isAvailable" in status) {
      return Boolean(Reflect.get(status, "isAvailable"));
    }
    return true;
  } catch {
    return true;
  }
}

async function authenticateBiometric(reason: string): Promise<boolean> {
  if (!isTauriRuntime()) {
    return false;
  }
  try {
    await invoke("plugin:biometric|authenticate", {
      reason,
      allowDeviceCredential: true,
    });
    return true;
  } catch {
    return false;
  }
}

function isAppLockArmed(): boolean {
  return isTauriRuntime() && isAppLockEnabled();
}

export interface AppLockMachineDeps {
  isArmed: () => boolean;
  checkAvailable: () => Promise<boolean>;
  authenticate: () => Promise<boolean>;
}

export interface AppLockMachine {
  state: () => AppLockState;
  enforce: () => Promise<void>;
  retry: () => Promise<void>;
  unlockForTesting?: () => void;
}

export function createAppLockMachine(
  deps: AppLockMachineDeps,
  onChange?: (state: AppLockState) => void,
): AppLockMachine {
  let current: AppLockState = deps.isArmed() ? "locked" : "unlocked";

  const set = (next: AppLockState): void => {
    if (next === current) {
      return;
    }
    current = next;
    onChange?.(next);
  };

  const attempt = async (): Promise<void> => {
    set("authenticating");
    const ok = await deps.authenticate();
    set(ok ? "unlocked" : "locked");
  };

  const enforce = async (): Promise<void> => {
    if (!deps.isArmed()) {
      set("unlocked");
      return;
    }
    if (current === "authenticating") {
      return;
    }
    const available = await deps.checkAvailable();
    if (!available) {
      set("unlocked");
      return;
    }
    await attempt();
  };

  const retry = async (): Promise<void> => {
    if (!deps.isArmed()) {
      set("unlocked");
      return;
    }
    await attempt();
  };

  return {
    state: () => current,
    enforce,
    retry,
    unlockForTesting: () => set("unlocked"),
  };
}

export interface AppLockProviderProps {
  isArmed?: () => boolean;
  checkAvailable?: () => Promise<boolean>;
  authenticate?: () => Promise<boolean>;
  onSignOut?: () => void | Promise<void>;
  translate?: (descriptor: MessageDescriptor) => string;
}

export const AppLockProvider: ParentComponent<AppLockProviderProps> = (props) => {
  const [state, setState] = createSignal<AppLockState>("unlocked");

  const MSG_LOCKED = msg`App locked`;
  const MSG_VERIFY = msg`Verify your identity to continue.`;
  const MSG_UNLOCK = msg`Unlock`;
  const MSG_SIGNOUT = msg`Sign out`;
  const MSG_UNLOCK_TO_CONTINUE = msg`Unlock to continue`;

  const tr = (d: MessageDescriptor) => (props.translate ? props.translate(d) : toText(d));

  const machine = createAppLockMachine(
    {
      isArmed: props.isArmed ?? isAppLockArmed,
      checkAvailable: props.checkAvailable ?? checkBiometricAvailable,
      authenticate: props.authenticate ?? (() => authenticateBiometric(tr(MSG_UNLOCK_TO_CONTINUE))),
    },
    (s) => {
      setState(s);
    },
  );

  setState(machine.state());

  onMount(() => {
    if (!isTauriRuntime()) {
      return;
    }

    void machine.enforce();

    const onVisible = (): void => {
      if (document.visibilityState === "visible") {
        void machine.enforce();
      }
    };
    const onFocus = (): void => {
      void machine.enforce();
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);

    onCleanup(() => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    });
  });

  const handleSignOut = async () => {
    if (props.onSignOut) {
      await props.onSignOut();
    }
  };

  return (
    <>
      {props.children}
      <Show when={state() !== "unlocked"}>
        <Portal>
          <div
            class="fixed inset-0 z-[100] flex items-center justify-center bg-background p-4"
            data-testid="app-lock-overlay"
          >
            <div class="w-full max-w-sm p-8 flex flex-col items-center text-center space-y-6 rounded-2xl border bg-card text-card-foreground shadow-lg">
              <div class="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Fingerprint class="h-8 w-8" />
              </div>
              <div class="space-y-2">
                <h2 class="text-2xl font-bold tracking-tight">{tr(MSG_LOCKED)}</h2>
                <p class="text-sm text-muted-foreground">{tr(MSG_VERIFY)}</p>
              </div>
              <div class="flex w-full flex-col gap-3">
                <button
                  type="button"
                  class="h-11 w-full gap-2 rounded-2xl bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center justify-center text-sm font-medium transition-colors"
                  disabled={state() === "authenticating"}
                  onClick={() => {
                    void machine.retry();
                  }}
                  data-testid="app-lock-retry"
                >
                  <Fingerprint class="h-4 w-4 mr-2" />
                  {tr(MSG_UNLOCK)}
                </button>
                <button
                  type="button"
                  class="h-11 w-full gap-2 rounded-2xl hover:bg-accent hover:text-accent-foreground inline-flex items-center justify-center text-sm font-medium transition-colors"
                  onClick={() => {
                    void handleSignOut();
                  }}
                  data-testid="app-lock-signout"
                >
                  <LogOut class="h-4 w-4 mr-2" />
                  <span>{tr(MSG_SIGNOUT)}</span>
                </button>
              </div>
            </div>
          </div>
        </Portal>
      </Show>
    </>
  );
};
